import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, ArrowUp, Globe, Lock, Eye } from "lucide-react";
import CorrelationComments from "./CorrelationComments";
import CorrelationShareMenu from "./CorrelationShareMenu";
import MessageButton from "./MessageButton";
import UserLink from "./UserLink";
import WeeklyItemCardThumb, { isRenderableCardData } from "./WeeklyItemCardThumb";

/**
 * Floating overlay that displays a correlation's full breakdown.
 *
 * Used from Weekly (when a reader clicks "Read the full breakdown")
 * and from Your Saves (when a user clicks any of their saved items).
 *
 * Closing: X button, Escape key, or backdrop click. Body scroll is locked
 * while open. The card itself is scrollable for very long responses.
 *
 * Props:
 *   item        — the correlation/save shape (title, bullets, response,
 *                 user_handle, user_name, source_type, bumps_count,
 *                 views_count, reactions_count, created_at, visibility)
 *   onClose     — fn() called when the user dismisses
 *   conceptLink — if set, render a "Open concept page →" link (only for
 *                 saves of kind: "concept" — points at /concept/{id})
 *   weeklyLink  — if set, render a "View in Weekly →" link. Used from Saves
 *                 when the saved correlation has been posted to the community
 *                 feed (visibility === "public"). Closes the modal on click
 *                 so the deep-link can scroll the underlying page.
 */
export default function CorrelationModal({ item, onClose, conceptLink, weeklyLink, onCommentCountChange }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!item) return null;
  const summary = (item.response || item.preview || "").trim();
  const hasCard = isRenderableCardData(item.card_data);
  // Suppress the plain bullet list when a share card is present — they duplicate.
  const hasBullets = !hasCard && item.bullets && item.bullets.length > 0;
  const dateStr = item.created_at ? new Date(item.created_at).toLocaleDateString() : "";

  // Render through a portal mounted on <body> so the modal escapes any
  // ancestor with a `transform` (e.g. PullToRefresh's wrapper). Without the
  // portal, `position: fixed` gets re-bound to the transformed ancestor and
  // the card lands wherever in the long scroll the ancestor happens to be,
  // not in the viewport — the bug that hid this modal entirely.
  return createPortal(
    <div
      data-testid="correlation-modal"
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-[70] flex items-center justify-center px-3 py-6 sm:py-12 bg-foreground/30 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <div
        className="paper w-full max-w-2xl max-h-[88vh] overflow-y-auto shadow-2xl relative animate-in zoom-in-95 duration-200"
        // Stop propagation handled by the wrapper's e.target check above.
      >
        {/* CLOSE */}
        <button
          type="button"
          data-testid="correlation-modal-close"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--accent)/0.08)] transition-colors z-10"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-10">
          {/* META ROW */}
          <div className="flex items-center gap-3 flex-wrap mb-4">
            {item.source_type && <span className={`tag tag-${item.source_type}`}>{item.source_type}</span>}
            <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/75 flex items-center gap-1.5">
              {item.visibility === "public"
                ? <Globe size={11} className="text-[hsl(var(--primary))]" />
                : <Lock size={11} className="text-muted-foreground" />}
              {item.visibility || "private"}
              {dateStr && <span className="mx-1">·</span>}
              {dateStr}
            </span>
            {(item.user_handle || item.user_name) && (
              <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/75 ml-auto inline-flex items-center gap-2 flex-wrap">
                by{" "}
                <UserLink
                  handle={item.user_handle}
                  userId={item.user_id}
                  name={item.user_name}
                  showAt
                  className="text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
                  onClick={onClose}
                  testid="modal-author"
                />
                {(item.user_handle || item.user_id) && (
                  <MessageButton
                    slug={item.user_handle || item.user_id}
                    ownerUserId={item.user_id}
                    testid="modal-msg-author"
                  />
                )}
              </span>
            )}
          </div>

          {/* TITLE */}
          <h2 className="serif text-3xl sm:text-4xl text-foreground leading-tight">{item.title}</h2>

          {/* SHAREABLE CARD — inline when the author attached one on save. */}
          {hasCard && (
            <div className="mt-6">
              <WeeklyItemCardThumb item={item} />
            </div>
          )}

          {/* AUTHOR CAPTION — the framing line the author wrote when sharing */}
          {item.caption && (
            <blockquote
              data-testid="correlation-modal-caption"
              className="mt-5 pl-5 border-l-2 border-[hsl(var(--primary))] text-foreground/90 serif italic text-[17px] sm:text-[18px] leading-relaxed whitespace-pre-wrap"
            >
              {item.caption}
            </blockquote>
          )}

          {/* BULLETS — the flashcard */}
          {hasBullets && (
            <div className="mt-6 hairline p-5">
              <div className="label mb-3">Where they overlap</div>
              <ul className="space-y-2.5">
                {item.bullets.map((b, i) => (
                  // Scoped to a single item.id inside this modal — bullets
                  // for a given saved correlation never reorder or splice,
                  // so `${item.id}-b-${i}` is stable across modal reopens.
                  <li key={`${item.id || item.title}-b-${i}`} className="flex gap-3 text-foreground/90 leading-relaxed">
                    <span className="text-[hsl(var(--primary))] serif text-lg leading-none mt-1">◇</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* FULL BODY */}
          {summary && (
            <div className="mt-6">
              <div className="label mb-3">The full breakdown</div>
              <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/90">
                {summary}
              </div>
            </div>
          )}

          {/* JUMP-TO-WEEKLY CTA — shown when this save was posted to the community feed */}
          {weeklyLink && (
            <div className="mt-8" data-testid="correlation-modal-weekly-cta">
              <Link
                to={weeklyLink}
                onClick={onClose}
                data-testid="correlation-modal-weekly-link"
                className="btn-gold inline-flex items-center gap-2"
              >
                View in Weekly →
              </Link>
              <p className="mt-3 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                See bumps, reactions, and any conversation around this correlation.
              </p>
            </div>
          )}

          {/* ENGAGEMENT FOOTER (read-only inside modal) */}
          {(item.bumps_count != null || item.reactions_count != null || item.views_count != null) && (
            <div className="mt-8 pt-6 hairline-t flex items-center gap-5 flex-wrap text-muted-foreground">
              <span className="inline-flex items-center gap-1.5 mono text-[11px] tabular-nums">
                <ArrowUp size={12} strokeWidth={2.25} />
                {item.bumps_count || 0} bumps
              </span>
              <span className="inline-flex items-center gap-1.5 mono text-[11px] tabular-nums">
                <Eye size={12} strokeWidth={2} />
                {item.views_count || 0} views
              </span>
              <span className="mono text-[11px] tabular-nums">{item.reactions_count || 0} reactions</span>
              {item.id && item.visibility === "public" && (
                <CorrelationShareMenu correlation={item} testIdPrefix="modal-share" />
              )}
            </div>
          )}

          {/* OPEN CONCEPT PAGE — independent of engagement counts (some concept-kind
              saves have no bumps/views/reactions and were hidden by the old gate). */}
          {conceptLink && (
            <div className="mt-8 pt-6 hairline-t flex">
              <Link
                to={conceptLink}
                onClick={onClose}
                data-testid="correlation-modal-concept-link"
                className="mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--accent))] link-underline ml-auto"
              >
                Open concept page →
              </Link>
            </div>
          )}

          {/* THREADED COMMENTS — only rendered when the modal knows its correlation id */}
          {item.id && (
            <CorrelationComments
              correlationId={item.id}
              onCountChange={(delta) => onCommentCountChange?.(item.id, delta)}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
